/**
 * The graphs index — what "Brain" opens onto.
 *
 * A table of the graphs that exist, searchable, each row with a ⋯ menu. Nothing
 * is listed that you did not create: an empty table is the honest state of a new
 * install, not something to paper over with samples.
 */

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CopyButton,
  EmptyState,
  ErrorNotice,
  Loading,
  Modal,
  relativeTime,
} from '@/shared/components'
import { useToast } from '@/shared/components/toast'
import { DeleteDialog } from '../components/DeleteDialog'
import { RowMenu } from '../components/RowMenu'
import { Chip, Field, Mark, SearchBox } from '../components'
import { GenerateDialog } from '../generate/GenerateDialog'
import { graphsApi } from '../api/brain.api'
import { invalidateModel, useCreateGraph, useGraphs } from '../hooks'
import type { Graph } from '../types/brain.types'
import '../styles/brain.css'
import { Constellation } from '@/shared/components/Constellation'

export function GraphsPage() {
  const graphs = useGraphs()
  const navigate = useNavigate()

  const [search, setSearch] = useState('')
  const [creating, setCreating] = useState<'closed' | 'blank' | 'generate'>('closed')
  const [deleting, setDeleting] = useState<Graph | null>(null)

  const rows = useMemo(() => {
    const all = graphs.data ?? []
    const needle = search.trim().toLowerCase()
    if (!needle) return all
    return all.filter(
      (g) =>
        g.name.toLowerCase().includes(needle) ||
        (g.description ?? '').toLowerCase().includes(needle) ||
        g.graph_id.toLowerCase().includes(needle),
    )
  }, [graphs.data, search])

  return (
    <div className="cp-screen">
      <div className="cp-scroll">
      <div className="cp-toolbar hero">
        <Constellation />
        <h1>Knowledge graphs</h1>
        {graphs.data && (
          <Chip>
            {graphs.data.length} graph{graphs.data.length === 1 ? '' : 's'}
            {rows.length !== graphs.data.length && ` · ${rows.length} shown`}
          </Chip>
        )}
        <span className="cp-spacer" />
        {graphs.data && graphs.data.length > 0 && (
          <SearchBox value={search} onChange={setSearch} placeholder="Search graphs…" />
        )}
        <button className="btn" onClick={() => setCreating('blank')}>
          New graph
        </button>
        <button className="btn btn-primary" onClick={() => setCreating('generate')}>
          Generate a graph
        </button>
        <div className="sub2">
          Each graph is its own model, sources, rules and golden records. Open one to enter its
          workspace.
        </div>
      </div>

      {graphs.isLoading && <Loading label="Loading graphs…" />}
      {graphs.error && <ErrorNotice error={graphs.error} onRetry={graphs.refetch} />}

      {graphs.data?.length === 0 && (
        <EmptyState
          title="No graphs yet"
          action={
            <div className="row-tight" style={{ justifyContent: 'center' }}>
              <button className="btn" onClick={() => setCreating('blank')}>
                Start empty
              </button>
              <button className="btn btn-primary" onClick={() => setCreating('generate')}>
                Describe what you need
              </button>
            </div>
          }
        >
          Describe what you need to keep track of and a model is drafted for you — entity types,
          relationships, and the quality, matching and survivorship rules to go with them. Or
          start empty and build it yourself. Either way it stays editable.
        </EmptyState>
      )}

      {graphs.data && graphs.data.length > 0 && rows.length === 0 && (
        <EmptyState title="Nothing matches that search">
          Try a different term, or clear the box.
        </EmptyState>
      )}

      {rows.length > 0 && (
        <div className="cp-card cp-tablewrap">
          <table className="cp-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>State</th>
                <th>Node types</th>
                <th>Relationships</th>
                <th>Records</th>
                <th>Updated</th>
                <th aria-label="Actions" style={{ width: 48 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((graph) => (
                <tr
                  key={graph.graph_id}
                  data-clickable="true"
                  onClick={() => navigate(`/brain/${graph.graph_id}?tab=graph`)}
                >
                  <td>
                    <span className="cp-rowtitle">
                      <Mark name={graph.name} id={graph.graph_id} />
                      <span className="truncate">{graph.name}</span>
                    </span>
                    {graph.description && (
                      <div className="small faint truncate" style={{ maxWidth: 340, marginTop: 2 }}>
                        {graph.description}
                      </div>
                    )}
                    <div
                      className="row-tight small faint"
                      style={{ marginTop: 3 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <code className="mono">{graph.graph_id}</code>
                      <CopyButton value={graph.graph_id} label="Copy id" />
                    </div>
                  </td>
                  <td>
                    {graph.published_version ? (
                      <Chip tone="live" dot>
                        {graph.published_version}
                      </Chip>
                    ) : (
                      <Chip>draft</Chip>
                    )}
                  </td>
                  <td className="num">
                    {graph.node_type_count ?? 0}
                    {(graph.node_type_count ?? 0) === 0 && (
                      <span style={{ marginLeft: 6 }}>
                        <Chip tone="warn">empty</Chip>
                      </span>
                    )}
                  </td>
                  <td className="num">{graph.edge_type_count ?? 0}</td>
                  <td className="num">{(graph.record_count ?? 0).toLocaleString()}</td>
                  <td className="muted small">{relativeTime(graph.updated_at)}</td>
                  <td>
                    <RowMenu
                      label={`Actions for ${graph.name}`}
                      items={[
                        {
                          label: 'Open workspace',
                          onSelect: () => navigate(`/brain/${graph.graph_id}?tab=graph`),
                        },
                        {
                          label: 'Node tables',
                          onSelect: () => navigate(`/objects/${graph.graph_id}`),
                          disabled: (graph.node_type_count ?? 0) === 0,
                        },
                        {
                          label: 'Sources',
                          onSelect: () => navigate(`/brain/${graph.graph_id}?tab=sources`),
                        },
                        {
                          label: 'Delete graph',
                          danger: true,
                          hint: 'and everything in it',
                          onSelect: () => setDeleting(graph),
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating === 'blank' && (
        <NewGraphDialog
          onClose={() => setCreating('closed')}
          onCreated={(id) => {
            setCreating('closed')
            navigate(`/brain/${id}?tab=graph`)
          }}
        />
      )}
      {creating === 'generate' && (
        <GenerateDialog
          onClose={() => setCreating('closed')}
          onDone={(id) => {
            setCreating('closed')
            navigate(`/brain/${id}?tab=graph`)
          }}
        />
      )}

      {deleting && (
        <DeleteDialog
          title="Delete this graph"
          name={deleting.name}
          what="graph"
          previewPath={`/brain/graphs/${deleting.graph_id}/deletion-preview`}
          onConfirm={() => graphsApi.remove(deleting.graph_id)}
          onDeleted={() => {
            void invalidateModel(deleting.graph_id)
            void graphs.refetch()
          }}
          onClose={() => setDeleting(null)}
        />
      )}
      </div>
    </div>
  )
}

function NewGraphDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: (graphId: string) => void
}) {
  const create = useCreateGraph()
  const toast = useToast()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  return (
    <Modal
      title="New graph"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={create.isPending}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={create.isPending || !name.trim()}
            onClick={async () => {
              try {
                const graph = await create.mutateAsync({
                  name: name.trim(),
                  description: description.trim() || undefined,
                })
                toast.success(`Created ${graph.name}.`)
                onCreated(graph.graph_id)
              } catch (error) {
                toast.error(error)
              }
            }}
          >
            {create.isPending ? 'Creating…' : 'Create'}
          </button>
        </>
      }
    >
      <div className="stack">
        <p className="muted small" style={{ margin: 0 }}>
          Nothing is shared between graphs, so this is the right unit for a second domain — or
          for trying something out without disturbing what already works.
        </p>
        <Field label="Name" required>
          <input
            className="input"
            value={name}
            placeholder="Supply chain"
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label="What is it for?">
          <input
            className="input"
            value={description}
            placeholder="Suppliers, parts and the incidents raised against them."
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
      </div>
    </Modal>
  )
}
